<template>
  <div>
    <unroll-element
      v-for="(metavariables_dict_poid, index) in metavariables_dicts_poids"
      :title="stringified_dict(metavariables_dict_poid)"
      :available="data_instantiated[index]"
    >
      <div v-if="data_instantiated[index]">
        <b-table
          striped
          hover
          :items="csv_format_table_items[index]"
          :fields="csv_format_table_fields"
        >
        </b-table>
      </div>
    </unroll-element>

    <div v-if="csv_format">
      <b-form-group>
        <b-form-group>
          <label>
            <b> {{ $t('file_encoding') }} </b>
          </label>
          <b-form-select
            v-model="codec_lower"
            :options="coercioncodecoptions"
          />
        </b-form-group>

        <b-form-group>
          <label>
            <b> {{ $t('field_delimiter') }} </b>
          </label>
          <b-form-select
            v-model="coerciondelimiter"
            :options="coerciondelimiteroptions"
          />
        </b-form-group>
        <b-form-group>
          <label>
            <b> {{ $t('other_field_delimiter') }} </b>
          </label>
          <b-form-input
            v-model="coerciondelimiterother"
            type="text"
            :placeholder="$t('other_field_delimiter')"
            :disabled="coerciondelimiter !== null"
          />
        </b-form-group>

        <b-form-group>
          <label>
            <b> {{ $t('text_identifier') }} </b>
          </label>
          <b-form-select
            v-model="csv_format.quotechar"
            :options="coercionquotecharoptions"
          />
        </b-form-group>

        <b-form-group>
          <label>
            <b> {{ $t('escape_char') }} </b>
          </label>
          <b-form-select
            v-model="csv_format.escapechar"
            :options="coercionescapecharoptions"
          />
        </b-form-group>

        <b-form-group>
          <label>
            <b> {{ $t('double_quote_choice') }} </b>
          </label>
          <b-form-select
            v-model="csv_format.doublequote"
            :options="boolean_select_options"
          />
        </b-form-group>

        <b-form-group>
          <label>
            <b> {{ $t('skip_initial_space_choice') }} </b>
          </label>
          <b-form-select
            v-model="csv_format.skipinitialspace"
            :options="boolean_select_options"
          />
        </b-form-group>
      </b-form-group>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "keep_auto_value": "Keep automatically detected value",
    "keep_auto_detected_format": "Keep automatically detected CSV format",
    "file_encoding": "File encoding (codec)",
    "field_delimiter": "Field delimiter",
    "other_field_delimiter": "Other field delimiter",
    "text_identifier": "Text identifier (quote character)",
    "escape_char": "Escape character",
    "double_quote_choice": "Use double quote character",
    "skip_initial_space_choice": "Skip initial space in a field after the delimiter",
    "tab": "Tabulation",
    "space": "Space",
    "other": "Other...",
    "(none)": "(None)",
    "backslash": "Backslash"
  },
  "fr": {
    "keep_auto_value": "Conserver la valeur détectée automatiquement",
    "keep_auto_detected_format": "Conserver le format détecté automatiquement",
    "file_encoding": "Origine du fichier (codec / encoding)",
    "field_delimiter": "Séparateur de champs",
    "other_field_delimiter": "Autre séparateur de champs",
    "text_identifier": "Identificateur de texte",
    "escape_char": "Caractère d'échappement",
    "double_quote_choice": "Doublement de l'identificateur de texte",
    "skip_initial_space_choice": "Suppression de l'espace initial dans un champ après le séparateur",
    "tab": "Tabulation",
    "space": "Espace",
    "other": "Autre...",
    "(none)": "(Aucun)",
    "backslash": "Barre oblique inversée"
  }
}
</i18n>

<script>
import _ from 'lodash'
import { global_brick_mixin } from '@/components/mixin.js'
import UnrollElement from './unroll_element.vue'

// *****************************************************************************

function possible_encodings() {
  let encodings = [
    'ascii',
    'big5',
    'big5hkscs',
    'cp037',
    'cp424',
    'cp437',
    'cp500',
    'cp720',
    'cp737',
    'cp775',
    'cp850',
    'cp852',
    'cp855',
    'cp856',
    'cp857',
    'cp858',
    'cp860',
    'cp861',
    'cp862',
    'cp863',
    'cp864',
    'cp865',
    'cp866',
    'cp869',
    'cp874',
    'cp875',
    'cp932',
    'cp949',
    'cp950',
    'cp1006',
    'cp1026',
    'cp1140',
    'cp1250',
    'cp1251',
    'cp1252',
    'cp1253',
    'cp1254',
    'cp1255',
    'cp1256',
    'cp1257',
    'cp1258',
    'euc_jp',
    'euc_jis_2004',
    'euc_jisx0213',
    'euc_kr',
    'gb2312',
    'gbk',
    'gb18030',
    'hz',
    'iso2022_jp',
    'iso2022_jp_1',
    'iso2022_jp_2',
    'iso2022_jp_2004',
    'iso2022_jp_3',
    'iso2022_jp_ext',
    'iso2022_kr',
    'latin_1',
    'iso8859_2',
    'iso8859_3',
    'iso8859_4',
    'iso8859_5',
    'iso8859_6',
    'iso8859_7',
    'iso8859_8',
    'iso8859_9',
    'iso8859_10',
    'iso8859_11',
    'iso8859_13',
    'iso8859_14',
    'iso8859_15',
    'iso8859_16',
    'johab',
    'koi8_r',
    'koi8_u',
    'mac_cyrillic',
    'mac_greek',
    'mac_iceland',
    'mac_latin2',
    'mac_roman',
    'mac_turkish',
    'ptcp154',
    'shift_jis',
    'shift_jis_2004',
    'shift_jisx0213',
    'utf_32',
    'utf_32_be',
    'utf_32_le',
    'utf_16',
    'utf_16_be',
    'utf_16_le',
    'utf_7',
    'utf_8',
    'utf_8_sig',
  ]
  return encodings
}

// *****************************************************************************

export default {
  name: 'BrickFileSubcomponentCsvFormat',
  components: {
    UnrollElement,
  },
  mixins: [global_brick_mixin],
  props: ['value'],
  data() {
    let coercioncodecoptions = [
      {
        value: 'keep_auto',
        text: this.$t('keep_auto_value'),
      },
    ]

    _.forEach(possible_encodings(), v => {
      coercioncodecoptions.push({
        value: v,
        text: v,
      })
    })

    return {
      coercioncodecoptions,
      coerciondelimiter: ';',
      csv_format: undefined,
    }
  },
  computed: {
    automatic_csv_format_infos() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_data(
            `automatic_csv_format_info_${
              (tracking_info || {}).automatic_csv_format_info_poid
            }`,
            (tracking_info || {}).automatic_csv_format_info_poid
          ) || {}
      )

      return res
    },

    automatic_csv_format_infos_instantiated() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_status(
            `automatic_csv_format_info_instantiated_${
              (tracking_info || {}).automatic_csv_format_info_poid
            }`,
            (tracking_info || {}).automatic_csv_format_info_poid
          ) || {}
      )
      return res
    },

    initial_csv_format_infos() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_data(
            `initial_csv_format_info_${
              (tracking_info || {}).csv_format_info_poid
            }`,
            (tracking_info || {}).csv_format_info_poid
          ) || {}
      )

      return res
    },

    initial_csv_format_infos_instantiated() {
      let res = _.map(
        this.tracking_infos,
        tracking_info =>
          this.fetch_poid_status(
            `initial_csv_format_info_instantiated_${
              (tracking_info || {}).csv_format_info_poid
            }`,
            (tracking_info || {}).csv_format_info_poid
          ) || {}
      )
      return res
    },

    data_instantiated() {
      let res = _.zip(
        this.automatic_csv_format_infos_instantiated,
        this.initial_csv_format_infos_instantiated
      )
      res = _.map(res, elt => _.every(elt))
      return res
    },

    coerciondelimiteroptions() {
      let res = [
        { value: 'keep_auto', text: this.$t('keep_auto_value') },
        { value: ';', text: this.$t('semicolon') + ' (;)' },
        { value: '\t', text: this.$t('tab') + ' (\u00A0\u00A0\u00A0\u00A0)' },
        { value: ',', text: this.$t('comma') + ' (,)' },
        { value: ' ', text: this.$t('space') + ' ( )' },
        { value: null, text: this.$t('other') },
      ]
      return res
    },
    coercionquotecharoptions() {
      let res = [
        { value: 'keep_auto', text: this.$t('keep_auto_value') },
        { value: '"', text: '"' },
        { value: "'", text: "'" },
        { value: null, text: this.$t('(none)') },
      ]
      return res
    },
    coercionescapecharoptions() {
      let res = [
        { value: 'keep_auto', text: this.$t('keep_auto_value') },
        { value: '\\', text: this.$t('backslash') + ' (\\)' },
        { value: null, text: this.$t('(none)') },
      ]
      return res
    },
    boolean_select_options() {
      let res = [
        { value: 'keep_auto', text: this.$t('keep_auto_value') },
        { value: true, text: this.$t('true') },
        { value: false, text: this.$t('false') },
      ]
      return res
    },
    codec_lower: {
      get() {
        if (this.csv_format && this.csv_format.codec) {
          return this.csv_format.codec.toLowerCase()
        } else {
          return ''
        }
      },
      set(nv) {
        this.csv_format.codec = nv.toLowerCase()
      },
    },
    coerciondelimiter_displayer() {
      return _.mapValues(
        _.keyBy(this.coerciondelimiteroptions, 'value'),
        'text'
      )
    },
    coercionquotechar_displayer() {
      return _.mapValues(
        _.keyBy(this.coercionquotecharoptions, 'value'),
        'text'
      )
    },
    coercionescapechar_displayer() {
      let res = _.mapValues(
        _.keyBy(this.coercionescapecharoptions, 'value'),
        'text'
      )
      return res
    },

    csv_format_parameters_list() {
      let res = [
        { label: this.$t('file_encoding'), tracking_info_value: 'codec' },
        { label: this.$t('field_delimiter'), tracking_info_value: 'delimiter' },
        { label: this.$t('text_identifier'), tracking_info_value: 'quotechar' },
        { label: this.$t('escape_char'), tracking_info_value: 'escapechar' },
        {
          label: this.$t('double_quote_choice'),
          tracking_info_value: 'doublequote',
        },
        {
          label: this.$t('skip_initial_space_choice'),
          tracking_info_value: 'skipinitialspace',
        },
      ]
      return res
    },

    csv_format_table_fields() {
      let res = [
        { key: 'format_parameter', label: this.$t('format_parameter') },
        { key: 'auto_detected_value', label: this.$t('auto_detected_value') },
        { key: 'final_value', label: this.$t('final_value') },
      ]
      return res
    },
    csv_format_table_items() {
      let self = this

      let parameter_key_to_displayer = {
        delimiter: this.coerciondelimiter_displayer,
        quotechar: this.coercionquotechar_displayer,
        escapechar: this.coercionescapechar_displayer,
      }

      function construct_table_item(elt, index, poid_index) {
        let auto_detected_key =
          self.csv_format_parameters_list[index].tracking_info_value

        let auto_detected_value =
          self.automatic_csv_format_infos[poid_index][auto_detected_key]

        if (
          _.indexOf(
            Object.keys(parameter_key_to_displayer),
            auto_detected_key
          ) !== -1
        ) {
          auto_detected_value = ((parameter_key_to_displayer || {})[
            auto_detected_key
          ] || {})[auto_detected_value]
        }

        let final_key =
          self.csv_format_parameters_list[index].tracking_info_value

        let final_value = self.initial_csv_format_infos[poid_index][final_key]

        if (
          _.indexOf(Object.keys(parameter_key_to_displayer), final_key) !== -1
        ) {
          final_value = ((parameter_key_to_displayer || {})[final_key] || {})[
            final_value
          ]
        }

        return {
          format_parameter: self.csv_format_parameters_list[index].label,
          auto_detected_value,
          final_value,
        }
      }

      let res = _.map(
        this.automatic_csv_format_infos,
        (automatic_csv_format_info, poid_index) =>
          _.map(this.csv_format_parameters_list, (elt, index) =>
            construct_table_item(elt, index, poid_index)
          )
      )

      return res
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(v) {
        if (!_.isEqual(v, this.csv_format)) {
          this.csv_format = _.cloneDeep(v)
        }
      },
    },
    csv_format: {
      immediate: true,
      deep: true,
      handler(new_val) {
        new_val = new_val || {}
        if (_.includes([';', '\t', ',', ' ', 'keep_auto'], new_val.delimiter)) {
          this.coerciondelimiter = new_val.delimiter
          this.coerciondelimiterother = ''
        } else {
          this.coerciondelimiter = null
          this.coerciondelimiterother = new_val.delimiter
        }
        this.$emit('input', _.cloneDeep(new_val))
      },
    },

    coerciondelimiter(new_val) {
      if (_.includes([';', '\t', ',', ' ', 'keep_auto'], new_val)) {
        this.csv_format.delimiter = new_val
      }
    },

    coerciondelimiterother(new_val) {
      if (
        !_.includes([';', '\t', ',', ' ', 'keep_auto'], this.coerciondelimiter)
      ) {
        this.csv_format.delimiter = new_val
      }
    },
  },
}
</script>
